import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExportData from 'highcharts/modules/export-data';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsNoDataToDisplay from 'highcharts/modules/no-data-to-display';
import moment from 'moment-timezone';
import { RefObject } from 'react';
import { colors, fontSizes, fontWeights } from 'ui/theme';
import { HEIGHT_OF_CONVERSION_CONTENT } from '../constants';
import { useTimeFiltersContext } from '../context';
import { LocalConversionEventStateType } from '../types';
import { getMultiConversionSymbol } from '../utils';

if (typeof Highcharts === 'object') {
  HighchartsExporting(Highcharts);
  HighchartsExportData(Highcharts);
  HighchartsNoDataToDisplay(Highcharts);
}

interface Props {
  conversions: LocalConversionEventStateType[];
  innerRef: RefObject<HighchartsReact>;
}

const dateTimeLabelFormats = {
  minute: '%l:%M%P',
  hour: '%l:%M%P', // 1:05pm
  day: '%b %e', // June 6
};

const getHighchartOptions = (conversions: LocalConversionEventStateType[]): Record<string, any> => {
  return {
    chart: {
      height: HEIGHT_OF_CONVERSION_CONTENT,
      animation: false,
      type: 'line',
    },
    title: {
      text: undefined,
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    lang: {
      noData: 'Select a metric to view its performance on the chart ',
    },
    noData: {
      style: {
        fontWeight: fontWeights.bold,
        fontSize: fontSizes.s14,
        color: colors.grey80,
      },
    },
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats,
      lineColor: colors.grey15,
    },
    yAxis: {
      lineWidth: 1,
      lineColor: colors.grey15,
      gridLineColor: colors.grey15,
      gridLineDashStyle: 'ShortDash',
      max: Math.max(...conversions.map(({ maxEventValueOverTime }) => maxEventValueOverTime), 0),
      endOnTick: false,
      title: {
        text: null,
      },
      stackLabels: {
        enabled: true,
        style: {
          color: colors.textPrimary,
          fontSize: fontSizes.s12,
          fontWeight: fontWeights.regular,
        },
      },
    },
    plotOptions: {
      line: {
        states: {
          inactive: {
            enabled: false,
          },
        },
      },
    },
    tooltip: {
      pointFormatter: function () {
        const {
          graphic: { symbolName = '' } = {},
          series: { name = '' } = {},
          color,
          y,
        } = this;

        return `
          <span">
            <span style="color: ${color};">
              ${getMultiConversionSymbol(symbolName)}
            </span>
            <span>
              ${name}: ${y}
            </span>
          <span>`;
      },
      useHTML: true,
      dateTimeLabelFormats,
    },
    series: conversions.map(({ eventName, color, shape, histogram, selected }) => {
      return {
        name: eventName,
        color,
        visible: selected,
        data: histogram?.buckets.map(({ time, value }) => [time, value]),
        marker: {
          symbol: shape,
          enabled: true,
        },
      };
    }),
  };
};

export const ConversionsLineChart = ({ conversions, innerRef }: Props) => {
  const { timezone } = useTimeFiltersContext();
  const options = getHighchartOptions(conversions);

  if (timezone) {
    // Highchart use moment to deduce timezone offset.
    window.moment = moment;
    options.time = {
      timezone,
    };
  }

  return (
    <HighchartsReact
      options={options}
      ref={innerRef}
      containerProps={{ style: { width: '100%', height: '100%' } }}
    />
  );
};
